import {
  Search,
  ListFilter,
  RotateCcw,
  Package,
  Clock,
  DollarSign,
  Boxes,
  Signal,
} from "lucide-react";
import { useRef, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import ARHCustomDataTable from "../../components/dataTable/ARHCustomDataTable.jsx";
import ARHAdvanceFilterModal from "../../components/application/date-picker/FilterModal.jsx";
import { getProductsBasicInfo } from "../../services/product_management_service.js";
import { handleSetSearch } from "../../utils/searchDebouncing.js";

// Data used to build the filter modal config
const PRODUCT_FOR_OPTIONS = ["TOPUP", "BUNDLE", "GAMES", "SOCIAL"];
const TYPE_OPTIONS        = ["DATA", "VOICE"];
const VALIDITY_OPTIONS    = ["HOURLY", "DAILY", "WEEKLY", "BI_WEEKLY", "MONTHLY"];
const PREFIX_OPTIONS      = [70, 71, 73, 74, 72, 76, 77, 78, 79];

// prefix → operator key (used for labels inside the filter modal)
const PREFIX_TO_OPERATOR = {
  70: "awcc",      71: "awcc",
  73: "etisalat",  78: "etisalat",
  74: "salaam",
  72: "roshan",    79: "roshan",
  76: "mtn",       77: "mtn",
};

function ProductsList() {
  const { t } = useTranslation();
  const searchRef = useRef(null);
  const [refetch, setRefetch] = useState(false);
  const [isFilterModalOpen, setFilterModalOpen] = useState(false);

  const [filter, setFilter] = useState({
    search: "",
    limit: 10,
    page: 1,
    status: "",
    productFor: "",
    type: "",
    validity: "",
    prefix: "",
  });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["productsBasic", filter, refetch],
    queryFn: () => getProductsBasicInfo(filter),
    keepPreviousData: true,
  });

  const handleRefetch = () => setRefetch((prev) => !prev);

  const handleSearchChange = (value) =>
    setFilter((prev) => ({ ...prev, search: value, page: 1 }));

  const handlePageChange  = (page)  => setFilter((prev) => ({ ...prev, page }));
  const handleLimitChange = (limit) => setFilter((prev) => ({ ...prev, limit, page: 1 }));

  // ---- Filter modal config ----
  const filterConfig = useMemo(
    () => [
      {
        key: "status",
        label: t("products.filters.status"),
        type: "dropdown",
        multiSelect: false,
        options: [
          { value: "", label: t("common.all") },
          { value: "active", label: t("products.filters.active") },
          { value: "inactive", label: t("products.filters.inactive") },
        ],
      },
      {
        key: "productFor",
        label: t("products.filters.productFor"),
        type: "dropdown",
        multiSelect: false,
        options: [
          { value: "", label: t("common.all") },
          ...PRODUCT_FOR_OPTIONS.map((pf) => ({
            value: pf,
            label: t(`products.productFor.${pf}`),
          })),
        ],
      },
      {
        key: "type",
        label: t("products.filters.type"),
        type: "dropdown",
        multiSelect: false,
        options: [
          { value: "", label: t("common.all") },
          ...TYPE_OPTIONS.map((type) => ({
            value: type,
            label: t(`products.type.${type}`),
          })),
        ],
      },
      {
        key: "validity",
        label: t("products.filters.validity"),
        type: "dropdown",
        multiSelect: false,
        options: [
          { value: "", label: t("common.all") },
          ...VALIDITY_OPTIONS.map((v) => ({
            value: v,
            label: t(`products.validity.${v}`),
          })),
        ],
      },
      {
        key: "prefix",
        label: t("products.filters.prefix"),
        type: "dropdown",
        multiSelect: false,
        options: [
          { value: "", label: t("common.all") },
          ...PREFIX_OPTIONS.map((p) => {
            const opKey = PREFIX_TO_OPERATOR[p];
            return {
              value: p,
              label: opKey ? `${p} — ${t(`products.operator.${opKey}`)}` : String(p),
            };
          }),
        ],
      },
      {
        key: "price",
        label: t("products.filters.price"),
        type: "range",
        min: 0,
        max: 10000,
        unit: "AFN",
      },
    ],
    [t]
  );

  // Initial values passed into the modal (so it re-opens with current filter state)
  const initialFilterValues = useMemo(
    () => ({
      status: filter.status,
      productFor: filter.productFor,
      type: filter.type,
      validity: filter.validity,
      prefix: filter.prefix,
      price: {
        min: filter.minPrice ?? 0,
        max: filter.maxPrice ?? 10000,
      },
    }),
    [filter]
  );

  // Handle Apply from modal
  const handleApplyFilters = (values) => {
    setFilter((prev) => ({
      ...prev,
      status: values.status || "",
      productFor: values.productFor || "",
      type: values.type || "",
      validity: values.validity || "",
      prefix: values.prefix ?? "",
      // range → minPrice / maxPrice (add to backend filter list if you want it applied)
      minPrice: values.price?.min ?? "",
      maxPrice: values.price?.max ?? "",
      page: 1,
    }));
  };

  // Count active filters for the button badge
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filter.status) count++;
    if (filter.productFor) count++;
    if (filter.type) count++;
    if (filter.validity) count++;
    if (filter.prefix !== "" && filter.prefix != null) count++;
    if (filter.minPrice || filter.maxPrice) count++;
    return count;
  }, [filter]);

  // ---- Color helpers ----
  const getProductForColor = (productFor) => {
    switch (productFor) {
      case "TOPUP":  return "text-blue-700 bg-blue-100";
      case "BUNDLE": return "text-purple-700 bg-purple-100";
      case "GAMES":  return "text-pink-700 bg-pink-100";
      case "SOCIAL": return "text-cyan-700 bg-cyan-100";
      default:       return "text-gray-700 bg-gray-100";
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "DATA":  return "text-emerald-700 bg-emerald-100";
      case "VOICE": return "text-orange-700 bg-orange-100";
      default:      return "text-gray-700 bg-gray-100";
    }
  };

  const getOperatorColor = (operatorKey) => {
    switch (operatorKey) {
      case "etisalat": return "text-green-700 bg-green-100";
      case "roshan":   return "text-red-700 bg-red-100";
      case "mtn":      return "text-yellow-700 bg-yellow-100";
      case "awcc":     return "text-blue-700 bg-blue-100";
      case "salaam":   return "text-purple-700 bg-purple-100";
      default:         return "text-gray-700 bg-gray-100";
    }
  };

  // ---- Columns ----
  const columns = [
    {
      key: "productName",
      label: t("products.columns.name"),
      render: (item) => (
        <div className="flex items-center gap-2 max-w-[260px] truncate">
          <Package size={14} className="text-primary-500 shrink-0" />
          <span className="truncate" title={item?.productName || "-"}>
            {item?.productName || "-"}
          </span>
        </div>
      ),
    },
    {
      key: "productFor",
      label: t("products.columns.productFor"),
      render: (item) => (
        <div className={`px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 justify-center ${getProductForColor(item?.productFor)}`}>
          <Boxes size={12} />
          {item?.productFor ? t(`products.productFor.${item.productFor}`) : "-"}
        </div>
      ),
    },
    {
      key: "price",
      label: t("products.columns.price"),
      render: (item) => (
        <div className="font-semibold flex items-center gap-1">
          <span>AF</span>
          {parseFloat(item?.price || 0).toFixed(2)}
        </div>
      ),
    },
    {
      key: "type",
      label: t("products.columns.type"),
      render: (item) => (
        <div className={`px-3 py-1 rounded-full text-xs font-medium inline-flex items-center justify-center ${getTypeColor(item?.type)}`}>
          {item?.type ? t(`products.type.${item.type}`) : "-"}
        </div>
      ),
    },
    {
      key: "validity",
      label: t("products.columns.validity"),
      render: (item) => (
        <div className="flex items-center gap-1 text-sm">
          <Clock size={14} className="text-gray-400" />
          {item?.validity ? t(`products.validity.${item.validity}`) : "-"}
        </div>
      ),
    },
    {
      key: "prefix",
      label: t("products.columns.prefix"),
      render: (item) => (
        <div className="text-sm font-mono">
          {item?.prefix != null ? item.prefix : "-"}
        </div>
      ),
    },
    {
      key: "operator",
      label: t("products.columns.operator"),
      render: (item) => (
        <div className={`px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 justify-center ${getOperatorColor(item?.operatorKey)}`}>
          <Signal size={12} />
          {item?.operatorKey ? t(`products.operator.${item.operatorKey}`) : "-"}
        </div>
      ),
    },
  ];

  // ---- Empty / error states ----
  const customEmptyState = (
    <div className="w-full min-h-[200px] flex flex-col items-center justify-center">
      <img src="/images/Search.png" alt={t("common.noDataFound")} className="w-[170px] h-[170px]" />
      <p className="mt-2 text-gray-500">{t("products.noProductsFound")}</p>
    </div>
  );

  const customErrorState = (
    <div className="w-full min-h-[200px] flex flex-col items-center justify-center">
      <img src="/images/Search.png" alt={t("common.error")} className="w-[170px] h-[170px]" />
      <div className="text-sm text-red-600 mb-2">
        {error?.message || t("products.loadFailed")}
      </div>
      <button
        onClick={handleRefetch}
        className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
      >
        {t("common.retry")}
      </button>
    </div>
  );

  return (
    <div className="w-full px-5 py-4 min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="w-full">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
              {t("products.title")}
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              {t("products.subtitle")}
            </p>
          </div>
        </div>
      </div>

      <div className="w-full flex flex-col md:flex-row items-center justify-between gap-5 mt-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center w-full md:w-[70%] gap-3 sm:gap-5">
          <div className="relative w-full sm:max-w-[371px]">
            <input
              ref={searchRef}
              onChange={(e) => handleSetSearch(handleSearchChange, e.target.value, searchRef)}
              type="text"
              placeholder={t("products.searchPlaceholder")}
              defaultValue={filter.search}
              className="w-full h-[44px] pl-10 pr-4 border border-[#E4E7EC] dark:border-gray-600 rounded-md bg-transparent dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <Search size={18} className="absolute top-[13px] left-3 text-gray-400" />
          </div>

          {/* Advanced Filter button */}
          <button
            onClick={() => setFilterModalOpen(true)}
            className="relative flex items-center gap-2 px-4 py-2 h-[44px] border border-[#E4E7EC] dark:border-gray-600 rounded-md bg-gradient-to-r from-[#F8F4FF] to-[#FAF9F3] dark:from-slate-800 dark:to-slate-800 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ListFilter size={20} className="text-[#667085] dark:text-gray-300" />
            <span className="text-[#667085] dark:text-gray-300 text-[14px]">
              {t("products.filter")}
            </span>
            {activeFilterCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-[#CD0C02] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        <div className="flex items-center justify-end w-full md:w-[30%] gap-3">
          <button
            onClick={handleRefetch}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-sm flex items-center gap-2 dark:text-white"
          >
            <RotateCcw size={16} />
            {t("products.refresh")}
          </button>
        </div>
      </div>

      {/* Advanced Filter Modal */}
      <ARHAdvanceFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onApply={handleApplyFilters}
        filterConfig={filterConfig}
        initialValues={initialFilterValues}
      />

      {/* Table */}
      <div className="mt-5">
        <ARHCustomDataTable
          data={data?.data || []}
          columns={columns}
          meta={data?.meta || {
            page: filter.page,
            pages: Math.ceil((data?.meta?.total || 0) / filter.limit) || 1,
            total: data?.meta?.total || 0,
            limit: filter.limit,
          }}
          loading={isLoading}
          isError={isError}
          error={error}
          onRetry={handleRefetch}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          searchQuery={filter.search}
          emptyState={customEmptyState}
          errorState={customErrorState}
          rowClassName="hover:bg-gray-50 dark:hover:bg-gray-700"
        />
      </div>
    </div>
  );
}

export default ProductsList;